/**
 * useComboboxSelector
 * Refatorado: Usa hook utilitário useClickOutside.
 */
import { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { useClickOutside } from '../../../hooks/useClickOutside';

export interface ComboboxOption {
    id: string;
    name: string;
}

interface UseComboboxSelectorProps<T extends ComboboxOption> {
    value: string;
    onChange: (value: string) => void;
    options: T[];
    onCreate?: (name: string) => Promise<void | string>;
    disabled?: boolean;
    /** Qual campo emitir para onChange: 'id' (UUID) ou 'name' (padrão) */
    valueKey?: 'id' | 'name';
}

export function useComboboxSelector<T extends ComboboxOption>({
    value,
    onChange,
    options,
    onCreate,
    disabled = false,
    valueKey = 'name'
}: UseComboboxSelectorProps<T>) {
    const [query, setQuery] = useState('');
    const [isOpen, setIsOpen] = useState(false);
    const [displayValue, setDisplayValue] = useState(value);
    const containerRef = useRef<HTMLDivElement>(null);

    // Hook utilitário
    useClickOutside(containerRef, () => setIsOpen(false), isOpen);

    // Sincroniza displayValue com value prop
    // Quando valueKey='id', o value é um UUID → busca o nome para exibição
    useEffect(() => {
        if (valueKey === 'id') {
            const option = Array.isArray(options)
                ? options.find(o => o.id === value)
                : undefined;
            setDisplayValue(option?.name ?? '');
        } else {
            setDisplayValue(value);
        }
    }, [value, valueKey, options]);

    // Garante que options seja sempre um array válido
    const safeOptions = useMemo(() =>
        Array.isArray(options)
            ? options.filter(opt => opt && typeof opt === 'object' && 'id' in opt && 'name' in opt)
            : [],
        [options]
    );

    // Filtra opções baseado na busca
    const filteredOptions = useMemo(() =>
        query === ''
            ? safeOptions
            : safeOptions.filter((option) =>
                option.name.toLowerCase().includes(query.toLowerCase())
            ),
        [query, safeOptions]
    );

    // Verifica se o termo digitado já existe
    const exactMatch = useMemo(() =>
        safeOptions.some(option => option.name.toLowerCase() === query.toLowerCase()),
        [query, safeOptions]
    );

    const handleSelect = useCallback((option: T) => {
        setDisplayValue(option.name);
        onChange(valueKey === 'id' ? option.id : option.name);
        setIsOpen(false);
        setQuery('');
    }, [onChange, valueKey]);

    const handleCreate = useCallback(async () => {
        if (!query.trim() || !onCreate) return;

        const queryValue = query.trim();

        try {
            // onCreate pode retornar o id (quando valueKey='id') ou o nome
            const result = await onCreate(queryValue);
            const valueToEmit = result ? String(result) : queryValue;

            // Sempre exibir o nome digitado, nunca o UUID
            setDisplayValue(queryValue);
            onChange(valueToEmit);
            setQuery('');
            setIsOpen(false);
        } catch (error) {
            console.error('Erro ao criar item:', error);
        }
    }, [query, onCreate, onChange]);

    const openDropdown = useCallback(() => {
        if (!disabled && !isOpen) {
            if (displayValue && query === '') {
                setQuery(displayValue);
            }
            setIsOpen(true);
        }
    }, [disabled, isOpen, displayValue, query]);

    const toggleDropdown = useCallback(() => {
        if (!disabled) setIsOpen(!isOpen);
    }, [disabled, isOpen]);

    return {
        // State
        query,
        setQuery,
        isOpen,
        setIsOpen,
        displayValue,
        containerRef,

        // Computed
        filteredOptions,
        exactMatch,
        canCreate: onCreate && query !== '' && !exactMatch,

        // Handlers
        handleSelect,
        handleCreate,
        openDropdown,
        toggleDropdown
    };
}
